import React, { useCallback, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, TextField, Box, InputAdornment, Divider,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService } from '../../../services/admissions/admissionApplicationService';
import { InterviewRoundWithRubric, InterviewRoundScore, InterviewRecommendation } from '../../../types/admissions';
import SearchableSelect from '../../common/SearchableSelect';
import { isScored, onStep, roundMax } from '../../../utils/admissionAssessment';
import RubricAnswerField, { answerValid } from './RubricAnswerField';

interface Props {
  open: boolean;
  applicationId: string;
  round: InterviewRoundWithRubric;
  existing?: InterviewRoundScore;
  onCancel: () => void;
  onSaved: () => Promise<void> | void;
}

export const RECOMMENDATION_LABEL: Record<InterviewRecommendation, string> = {
  strongly_recommend: 'Strongly recommend',
  recommend: 'Recommend',
  hold: 'Hold for review',
  do_not_recommend: 'Do not recommend',
};

const inRange = (v: string, max: number, step: number) => {
  if (v.trim() === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= max && onStep(n, step);
};

/**
 * One interview round, filled in like its paper sheet: every question in
 * order — marks for scored ones, an option or a note for the rest. With
 * scored questions the round total is their sum; without, the panel gives
 * a single mark.
 */
const InterviewRoundDialog: React.FC<Props> = ({ open, applicationId, round, existing, onCancel, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [busy, setBusy] = useState(false);
  const rubric = round.rubric || [];
  const hasScored = rubric.some(isScored);
  const step = round.markStep || 1;
  const max = roundMax(round);

  // One answer per question, as typed: marks, the chosen option or a note.
  const [answers, setAnswers] = useState<string[]>(() => {
    const prev = new Map((existing?.rubricScores || []).map(r => [r.criterion, r]));
    return rubric.map(c => {
      const p = prev.get(c.criterion);
      if (!p) return '';
      return isScored(c) ? (p.obtained != null ? String(p.obtained) : '') : (p.answer ?? '');
    });
  });
  const setAnswer = useCallback((i: number, v: string) => setAnswers(prev => prev.map((x, j) => (j === i ? v : x))), []);
  const [total, setTotal] = useState(existing && !hasScored ? String(existing.totalMarks) : '');
  const [recommendation, setRecommendation] = useState<string>(existing?.recommendation ?? '');
  const [interviewer, setInterviewer] = useState(existing?.interviewer ?? '');
  const [comments, setComments] = useState(existing?.comments ?? '');

  const rubricTotal = rubric.reduce((t, c, i) => t + (isScored(c) && answerValid(c, answers[i], step) ? Number(answers[i]) : 0), 0);
  const valid = rubric.every((c, i) => answerValid(c, answers[i], step)) && (hasScored || inRange(total, max, step));

  const save = async () => {
    setBusy(true);
    try {
      await admissionApplicationService.recordInterviewRound(applicationId, {
        roundKey: round.key,
        rubricScores: rubric.map((c, i) => (isScored(c)
          ? { criterion: c.criterion, maxMarks: c.maxMarks, obtained: Number(answers[i]) }
          : { criterion: c.criterion, maxMarks: 0, answer: answers[i].trim() || undefined })),
        ...(hasScored ? {} : { totalMarks: Number(total) }),
        recommendation: (recommendation || undefined) as InterviewRecommendation | undefined,
        interviewer: interviewer.trim() || undefined,
        comments: comments.trim() || undefined,
      });
      enqueueSnackbar(`${round.name} saved`, { variant: 'success' });
      await onSaved();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not save the interview', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const markField = (value: string, onChange: (v: string) => void, maxMarks: number, width = 150) => (
    <TextField
      size="small"
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/[^\d.]/g, ''))}
      error={value.trim() !== '' && !inRange(value, maxMarks, step)}
      inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
      InputProps={{ endAdornment: <InputAdornment position="end">/ {maxMarks}</InputAdornment> }}
      sx={{ width }}
    />
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 0.5 }}>
        <Typography variant="h6" fontWeight={700}>{round.name}</Typography>
        <Typography variant="body2" color="text.secondary">Out of {max}{step === 0.5 ? ' · half marks allowed' : ''}</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 2 }}>
          {rubric.length > 0 && (
            <Stack spacing={1.5}>
              {rubric.map((c, i) => (
                <RubricAnswerField key={c.criterion} item={c} index={i} value={answers[i]} step={step} onChange={setAnswer} />
              ))}
            </Stack>
          )}
          {hasScored ? (
            <>
              <Divider />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" fontWeight={700}>Round total</Typography>
                <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>{rubricTotal} / {max}</Typography>
              </Stack>
            </>
          ) : (
            <>
              {rubric.length > 0 && <Divider />}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Typography variant="body2" fontWeight={rubric.length ? 700 : 400} sx={{ flex: 1 }}>{rubric.length ? 'Round marks' : 'Marks'}</Typography>
                {markField(total, setTotal, max)}
              </Stack>
            </>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <SearchableSelect
              label="Recommendation"
              value={recommendation}
              onChange={setRecommendation}
              options={(Object.keys(RECOMMENDATION_LABEL) as InterviewRecommendation[]).map(k => ({ value: k, label: RECOMMENDATION_LABEL[k] }))}
              placeholder="Optional"
              fullWidth
              minWidth={0}
            />
            <TextField size="small" label={round.kind === 'observation' ? 'Observed by' : 'Interviewer'} value={interviewer} onChange={(e) => setInterviewer(e.target.value)} />
          </Box>
          <TextField
            label="Panel notes"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            multiline
            minRows={2}
            size="small"
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save} disabled={busy || !valid}>
          {busy ? 'Saving…' : 'Save round'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterviewRoundDialog;
